import React, { useState, Fragment } from 'react';
import axios from 'axios';
import qs from 'qs';
import ReCaptcha from 'react-google-recaptcha';
import { useAtom } from "jotai";
import ErrorMessageList from './ErrorMessageList';
import messageMap from './i18n/messageMap.json';
import { customerAtom, totalPriceAtom } from "./atoms";

const InputForm = React.memo(({ requiredFields, validate, onSuccessCallback }) => {
    const [customer, setCustomer] = useAtom(customerAtom);
    const [totalPrice] = useAtom(totalPriceAtom);
    const [errorMessages, setErrorMessages] = useState([]);
    const [processingRequest, setProcessingRequest] = useState(false);
    const [gRecaptchaResponse, setGRecaptchaResponse] = useState('');

    const handleNameChange = (event) => {
        setCustomer((prevCustomer) => ({...prevCustomer, name: event.currentTarget.value }))
    }

    const handlePhoneNumberChange = (event) => {
        setCustomer((prevCustomer) => ({...prevCustomer, phoneNumber: event.currentTarget.value }))
    }

    const handleEmailChange = (event) => {
        setCustomer((prevCustomer) => ({...prevCustomer, emailAddress: event.currentTarget.value }))
    }

    const updateRecaptchaResponse = (response) => {
        setGRecaptchaResponse(response);
    }

    const onSubmit = (event) => {
        event.preventDefault();
        const messages = [];
        const { name, phoneNumber, emailAddress } = customer;
        const validationPayload = {
            name,
            phoneNumber,
            emailAddress,
            orderTotal: totalPrice
        };
        let valid = true;
        setProcessingRequest(true);
        
        // Perform client-side validation (if validate prop is true)
        if (validate && requiredFields) {
            Object.keys(requiredFields).forEach((requiredField) => {
                if (customer[requiredField].length === 0) {
                    valid = false;
                    messages.push({
                        msg: messageMap[requiredField]
                    });
                }
            });
        }

        // If client-side validation is on, validate google ReCaptcha
        if (validate && gRecaptchaResponse !== null && gRecaptchaResponse.length === 0) {
            valid = false;
            messages.push({
                msg: messageMap['recaptcha']
            });
        } else {
            validationPayload['g-recaptcha-response'] = gRecaptchaResponse;
        }

        // If client-side validation failed,
        // do not make server-side call.
        // Show messages
        if (valid === false) {
            setProcessingRequest(false);
            setErrorMessages(messages);
        } else {
            axios.post('/passover-menu/api/validate', qs.stringify(validationPayload))
                .then(({ data }) => {
                    if (data.requestData) {
                        // Assign the fields returned
                        // to the values in Customer Store,
                        // since server-side is sanitizing the data
                        const { name, phoneNumber, emailAddress } = data.requestData;
                        setCustomer((prevCustomer) => ({
                            ...prevCustomer,
                            name, 
                            phoneNumber, 
                            emailAddress,
                        }))
                    }
                    if (onSuccessCallback) {
                        onSuccessCallback.call();
                    }
                })
                .catch((error) => {
                    console.error("### Menu Validation call failed: ", error);
                    if (error.response) {
                        const { data } = error.response;
                        if (data.errors) {
                            data.errors.forEach((msg) => messages.push(msg));
                        } else {
                            messages.push({
                                msg: messageMap['generic']
                            });
                        }
                    } else {
                        messages.push({
                            msg: messageMap['generic']
                        });
                    }
                })
                .finally(() => {
                    setProcessingRequest(false);
                    setErrorMessages(messages);
                });            
        }
    }

    let invalidFields = {};
    const { name, phoneNumber, emailAddress } = customer;
    return (
        <Fragment>
            <div className="message-container">
                <ErrorMessageList messages={errorMessages}/>
            </div>
            <form onSubmit={onSubmit} noValidate>
                 <div className="form-section">
                    <p>* indicates a required field</p>
                </div>
                <div className={`form-section ${invalidFields['name'] || ''}`}>
                    <label htmlFor="name">{`${requiredFields['name'] ? "*" : ""}`}Name:</label>
                    <input type="text" name="name" id="name" placeholder="Jane Doe" value={name} onChange={handleNameChange} />
                </div>
                <div className={`form-section ${invalidFields['phoneNumber'] || ''}`}>
                    <label htmlFor="phoneNumber">{`${requiredFields['phoneNumber'] ? "*" : ""}`}Phone Number:</label>
                    <input type="text" name="phoneNumber" id="phoneNumber" placeholder="123-456-7890" value={phoneNumber} onChange={handlePhoneNumberChange} />
                </div>
                <div className={`form-section ${invalidFields['emailAddress'] || ''}`}>
                    <label htmlFor="email">{`${requiredFields['emailAddress'] ? "*" : ""}`}Email:</label>
                    <input type="email" name="email" id="email" placeholder="jane.doe@email.com" value={emailAddress} onChange={handleEmailChange} />
                </div>
                <div className="form-section recaptcha-container">
                    <ReCaptcha
                        sitekey={GOOGLE_RECAPTCHA_KEY}
                        onChange={updateRecaptchaResponse}
                    />
                </div>
                <div className="form-section">
                    <div className={`form-submit-button-holder ${(processingRequest) ? 'disabled' : ''}`}>
                        <button
                            type="submit"
                            className="passover-submit-button form-submit-button"
                            disabled={processingRequest}>
                            {(processingRequest) ? 'processing...' : 'continue'}
                        </button>
                        <div className="form-submit-button-spinner fa fa-spinner fa-spin fa-2x"></div>
                    </div>
                </div>
            </form>
        </Fragment>
    )
});

export default InputForm;
